import React, { useEffect, useState } from "react";
import { api, errMsg, fmtMoney, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckSquare, Loader2, ShieldAlert } from "lucide-react";

const ACTION = {
  suppliers: "approve", spas: "approve", supplier_invoices: "approve",
  supplier_payments: "approve", lprc_transfers: "approve", customers: "approve",
  sales_orders: "approve", customer_payments: "verify", inventory_adjustments: "approve",
};
const EP = {
  suppliers: "suppliers", spas: "spas", supplier_invoices: "supplier-invoices",
  supplier_payments: "supplier-payments", lprc_transfers: "lprc-transfers",
  customers: "customers", sales_orders: "sales-orders", customer_payments: "customer-payments",
  inventory_adjustments: "inventory-adjustments",
};
const LABEL = {
  suppliers: "Supplier", spas: "SPA", supplier_invoices: "Supplier Invoice",
  supplier_payments: "Supplier Payment", lprc_transfers: "LPRC Transfer", customers: "Customer",
  sales_orders: "Sales Order", customer_payments: "Customer Payment", inventory_adjustments: "Inventory Adjustment",
};

export default function Approvals() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/approvals/pending").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const act = async (it) => {
    setBusy(it.id);
    try {
      await api.post(`/${EP[it.collection]}/${it.id}/${ACTION[it.collection]}`, {});
      toast.success(`${LABEL[it.collection]} ${it.reference} ${ACTION[it.collection] === "verify" ? "verified" : "approved"}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };

  if (!items) return <div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight text-slate-900">Approval Center</h1>
          <p className="text-sm text-slate-500 mt-0.5">Items awaiting your authorization · segregation of duties enforced</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-600 bg-white border border-slate-200 rounded-md px-3 py-1.5">
          <CheckSquare className="h-4 w-4 text-[#12346B]" /> {items.length} pending
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg py-16 text-center text-slate-400 text-sm">
          No items pending your approval.
        </div>
      ) : (
        <div className="space-y-2.5">
          {items.map((it) => {
            const ownRecord = it.created_by === user?.user_id;
            return (
              <div key={it.id} data-testid={`approval-item-${it.id}`}
                className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="mono text-sm font-semibold text-slate-900">{it.reference}</span>
                    <span className="text-[10px] uppercase tracking-wide text-slate-400 bg-slate-100 rounded px-1.5 py-0.5">{LABEL[it.collection]}</span>
                    <StatusBadge status={it.status} />
                  </div>
                  <div className="text-sm text-slate-500 mt-0.5 truncate">{it.party}</div>
                </div>
                <div className="text-right shrink-0">
                  {it.amount ? <div className="tabular font-semibold text-slate-800">{fmtMoney(it.amount)}</div> : null}
                  {it.quantity ? <div className="tabular text-xs text-slate-500">{fmtNum(it.quantity)} L</div> : null}
                </div>
                <div className="shrink-0">
                  {ownRecord ? (
                    <span className="flex items-center gap-1 text-xs text-orange-600" title="Segregation of duties">
                      <ShieldAlert className="h-4 w-4" /> Own record
                    </span>
                  ) : (
                    <Button size="sm" disabled={busy === it.id} onClick={() => act(it)}
                      data-testid={`btn-approve-${it.id}`} className="bg-[#12346B] hover:bg-[#12346B]/90">
                      {busy === it.id && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                      {ACTION[it.collection] === "verify" ? "Verify" : "Approve"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
